/**
 * Inputs: day date + optional edit id + kind query. Outputs: a new or updated event/idea on the shared trip.
 * Transfer type and booking certainty are independent. Pickup and destination are separate places.
 * Transfer pickup/arrival times are saved even when timing is after landing or when ready,
 * so the day timeline can order them. They stay labelled unconfirmed until the owner marks a fixed time.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { createId } from "@shared/ids.ts";
import { stayForDate } from "@shared/itinerary.ts";
import {
  previewEventRemoval,
  previewIdeaRemoval,
  removeEventFromTrip,
  removeIdeaFromTrip,
  syncLinkedBookingDates,
} from "@shared/plan-lifecycle.ts";
import { formatLongDate, todayInZone } from "@shared/time.ts";
import {
  applyEventPlanEdits,
  composeNotesFromEvent,
  resolvedPlanStatus,
  timingModeFromEvent,
  withSyncedBookingStatus,
  workingArrivalFromEvent,
  workingTimeFromEvent,
  type PlanStatus,
  type TimingMode,
} from "@shared/plan-status.ts";
import type { Idea, TripDocument } from "@shared/schema.ts";
import { useTrip } from "../../app/trip.tsx";
import { useSession } from "../../app/session.tsx";
import { emptyPlaceDraft, newPlaceFromMapsDraft, type PlaceDraftFields } from "@shared/maps-place.ts";
import { fillIfEmpty } from "@shared/maps-url.ts";
import { readMapsPlace } from "../../lib/api.ts";
import { AddPlanChooser, type PlanKind } from "../../components/AddPlanChooser.tsx";
import { PlanBookingFields } from "./PlanBookingFields.tsx";
import { applyPlanBooking, draftFromBooking, type PlanBookingDraft } from "@shared/plan-booking.ts";
import { PlaceLinkFields } from "./PlaceLinkFields.tsx";
import { RemoveRecord } from "../../components/RemoveRecord.tsx";

const KINDS: PlanKind[] = ["transfer", "excursion", "free_time", "idea"];
type TransferMethod = "grab_bolt" | "taxi" | "arranged_driver" | "other";

function parseKind(raw: string | null): PlanKind | null {
  if (raw && KINDS.includes(raw as PlanKind)) return raw as PlanKind;
  return null;
}

function PlaceSelect({
  label,
  value,
  onChange,
  trip,
  extra,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  trip: TripDocument;
  extra?: ReactNode;
}) {
  return (
    <>
      <label className="field">
        {label}
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Still to choose</option>
          {extra}
          <option value="__new__">Add a new place</option>
          {trip.places.map((place) => (
            <option key={place.id} value={place.id}>{place.name}</option>
          ))}
        </select>
      </label>
    </>
  );
}

export function ComposePage() {
  const { date } = useParams();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const kindParam = parseKind(params.get("kind"));
  const fromIdea = params.get("fromIdea");
  const navigate = useNavigate();
  const { role } = useSession();
  const { trip, save, saveState } = useTrip();
  const existingEvent = trip?.events.find((event) => event.id === editId);
  const existingIdea = trip?.ideas.find((idea) => idea.id === editId || idea.id === fromIdea);
  const linkedBooking = trip?.bookings.find((booking) => booking.id === existingEvent?.bookingId);
  const noteState = composeNotesFromEvent(existingEvent);

  const initialKind = useMemo((): PlanKind => {
    if (existingEvent?.kind === "transfer" || existingEvent?.kind === "excursion" || existingEvent?.kind === "free_time") {
      return existingEvent.kind;
    }
    if (existingIdea && !existingEvent) return "idea";
    return kindParam ?? "excursion";
  }, [existingEvent, existingIdea, kindParam]);

  const arrivalsThatDay = trip?.flights.filter((flight) => flight.arrival.local.slice(0, 10) === date) ?? [];
  const arrivalHint = arrivalsThatDay[arrivalsThatDay.length - 1];
  const stayHint = trip && date
    ? stayForDate(trip, date) ?? trip.stays.find((stay) => stay.startDate === date)
    : undefined;
  const [kindOverride, setKindOverride] = useState<PlanKind | null>(null);
  const kind = kindOverride ?? initialKind;
  const [title, setTitle] = useState(existingEvent?.title ?? linkedBooking?.title ?? existingIdea?.title ?? "");
  const [status, setStatus] = useState<PlanStatus>(
    existingEvent
      ? resolvedPlanStatus(existingEvent, linkedBooking)
      : existingIdea ? "idea" : "planned",
  );
  const [method, setMethod] = useState<TransferMethod>(existingEvent?.transferMethod ?? "grab_bolt");
  const [timing, setTiming] = useState<TimingMode>(
    existingEvent
      ? timingModeFromEvent(existingEvent)
      : arrivalHint
        ? "after_arrival"
        : "flexible",
  );
  const [time, setTime] = useState(existingEvent ? workingTimeFromEvent(existingEvent) : "");
  const [arrivalTime, setArrivalTime] = useState(existingEvent ? workingArrivalFromEvent(existingEvent) : "");
  const [fromPlaceId, setFromPlaceId] = useState(
    existingEvent?.fromPlaceId
    ?? (existingEvent?.fromAirport
      ? `__airport__:${existingEvent.fromAirport}`
      : arrivalHint
        ? `__airport__:${arrivalHint.arrival.airport}`
        : ""),
  );
  const [fromAirport, setFromAirport] = useState(existingEvent?.fromAirport ?? arrivalHint?.arrival.airport ?? "");
  const [toPlaceId, setToPlaceId] = useState(
    existingEvent?.toPlaceId
    ?? existingEvent?.meetingPlaceId
    ?? existingIdea?.placeId
    ?? stayHint?.placeId
    ?? "",
  );
  const [fromDraft, setFromDraft] = useState<PlaceDraftFields>(emptyPlaceDraft);
  const [toDraft, setToDraft] = useState<PlaceDraftFields>(emptyPlaceDraft);
  const [pickup, setPickup] = useState(existingEvent?.pickupInstruction ?? "");
  const [notes, setNotes] = useState(noteState.current || existingIdea?.notes || "");
  const [error, setError] = useState<string | null>(null);
  const [planDate, setPlanDate] = useState(existingEvent?.date ?? existingIdea?.date ?? date ?? "");
  const existingBooking = trip?.bookings.find((row) => row.id === existingEvent?.bookingId);
  const [bookingDraft, setBookingDraft] = useState<PlanBookingDraft>(() =>
    draftFromBooking(
      existingBooking,
      trip ? todayInZone(new Date(), trip.trip.homeTimezone) : "",
    ),
  );

  if (role !== "owner") {
    return (
      <main className="page">
        <h1 className="serif">Owner only</h1>
        <p>Family sessions can read everything but cannot edit.</p>
      </main>
    );
  }
  if (!trip || !date) return <main className="page">Missing day.</main>;

  const showChooser = !editId && !kindParam && !fromIdea;
  if (showChooser) {
    return (
      <main className="page">
        <Link className="btn-ghost" to={`/journey/day/${date}`}>Back</Link>
        <h1 className="serif">Add to this day</h1>
        <AddPlanChooser day={date} backTo={`/journey/day/${date}`} />
      </main>
    );
  }

  const current = trip;
  const day = planDate || date;
  const arrivalFlights = current.flights.filter((flight) => flight.arrival.local.slice(0, 10) === day);
  const arrivalFlight = arrivalFlights[arrivalFlights.length - 1];
  const departureFlight = current.flights.find((flight) => flight.departure.local.slice(0, 10) === day);
  const dayStay = stayForDate(current, day) ?? current.stays.find((stay) => stay.startDate === day);
  const suggestedTo = dayStay ? current.places.find((place) => place.id === dayStay.placeId) : undefined;

  async function enrichDraft(draft: PlaceDraftFields): Promise<PlaceDraftFields> {
    if (!draft.mapsUrl.trim()) return draft;
    if (draft.address.trim() && draft.name.trim()) return draft;
    try {
      const { hint } = await readMapsPlace(draft.mapsUrl.trim());
      return {
        ...draft,
        name: fillIfEmpty(draft.name, hint.name),
        address: fillIfEmpty(draft.address, hint.address),
        thaiName: fillIfEmpty(draft.thaiName, hint.thaiName),
        coordinates: draft.coordinates ?? hint.coordinates,
        lookupNote: hint.note,
      };
    } catch {
      return draft;
    }
  }

  async function resolvePlace(
    selected: string,
    draft: PlaceDraftFields,
    places: TripDocument["places"],
  ): Promise<{ id: string | null; places: TripDocument["places"] } | { error: string }> {
    if (!selected) return { id: null, places };
    if (selected.startsWith("__airport__:")) return { id: null, places };
    if (selected !== "__new__") return { id: selected, places };
    const filled = await enrichDraft(draft);
    if (!filled.name.trim()) return { error: "Name the new place, or leave it unchosen." };
    const id = createId("place");
    const asOf = todayInZone(new Date(), current.trip.diaryTimezone);
    return {
      id,
      places: [...places, newPlaceFromMapsDraft(id, filled, asOf)],
    };
  }

  async function onSubmit(formEvent: FormEvent) {
    formEvent.preventDefault();
    if (!title.trim()) {
      setError("Give it a name.");
      return;
    }
    setError(null);
    let places = current.places;
    const fromResolved = await resolvePlace(fromPlaceId, fromDraft, places);
    if ("error" in fromResolved) {
      setError(fromResolved.error);
      return;
    }
    places = fromResolved.places;
    const toResolved = await resolvePlace(toPlaceId, toDraft, places);
    if ("error" in toResolved) {
      setError(toResolved.error);
      return;
    }
    places = toResolved.places;
    const chosenFrom = fromResolved.id;
    const chosenTo = toResolved.id;
    const airport = fromPlaceId.startsWith("__airport__:")
      ? fromPlaceId.slice("__airport__:".length)
      : fromAirport || null;
    const laterNotes = noteState.later;
    const nextNotes = notes.trim() ? [notes.trim(), ...laterNotes] : laterNotes;

    if (kind === "idea") {
      const idea: Idea = {
        id: existingIdea?.id ?? createId("idea"),
        title: title.trim(),
        status: "idea",
        date: day,
        placeId: chosenTo || chosenFrom || existingIdea?.placeId || null,
        notes: notes.trim() || null,
        cost: existingIdea?.cost ?? null,
      };
      const ideas = existingIdea
        ? current.ideas.map((row) => (row.id === existingIdea.id ? idea : row))
        : [...current.ideas, idea];
      const result = await save({ ...current, places, ideas });
      if (!result.ok) {
        setError(result.message);
        return;
      }
      navigate(`/journey/day/${day}`);
      return;
    }

    const nextEvent = applyEventPlanEdits(existingEvent, {
      id: existingEvent?.id ?? createId("event"),
      kind,
      title: title.trim(),
      date: day,
      timezone: current.trip.diaryTimezone,
      status,
      timing,
      time,
      arrivalTime,
      meetingPlaceId: kind === "excursion" ? (chosenTo || null) : existingEvent?.meetingPlaceId ?? null,
      fromPlaceId: kind === "transfer" ? (chosenFrom || null) : existingEvent?.fromPlaceId ?? null,
      fromAirport: kind === "transfer" ? airport : existingEvent?.fromAirport ?? null,
      toPlaceId: kind === "transfer" ? (chosenTo || null) : (kind === "excursion" ? chosenTo || existingEvent?.toPlaceId || null : existingEvent?.toPlaceId ?? null),
      pickupInstruction: kind === "transfer" ? pickup || null : existingEvent?.pickupInstruction ?? null,
      transferMethod: kind === "transfer" ? method : existingEvent?.transferMethod ?? null,
      notes: nextNotes,
      bookingId: existingEvent?.bookingId ?? null,
      linkedFlightId: existingEvent?.linkedFlightId ?? (timing === "after_arrival" ? arrivalFlight?.id ?? null : existingEvent?.flightId ?? null),
      sortIndex: existingEvent?.sortIndex,
    });
    const events = existingEvent
      ? current.events.map((row) => (row.id === existingEvent.id ? nextEvent : row))
      : [...current.events, nextEvent];
    const ideas = fromIdea ? current.ideas.filter((row) => row.id !== fromIdea) : current.ideas;
    let nextDoc: TripDocument = { ...current, places, events, ideas };
    const linked = nextEvent.bookingId
      ? current.bookings.find((row) => row.id === nextEvent.bookingId)
      : undefined;
    const captureBooking = status === "booked"
      && (kind === "excursion" || kind === "transfer")
      && !linked?.cost;
    if (captureBooking) {
      const applied = applyPlanBooking(nextDoc, nextEvent, bookingDraft, new Date().toISOString());
      if ("error" in applied) {
        setError(applied.error);
        return;
      }
      nextDoc = applied.doc;
    }
    const linkedBookingId = nextDoc.events.find((row) => row.id === nextEvent.id)?.bookingId ?? nextEvent.bookingId;
    const bookings = withSyncedBookingStatus(nextDoc.bookings, linkedBookingId, status).map((booking) => (
      kind === "excursion" && booking.id === linkedBookingId
        ? { ...booking, title: title.trim() }
        : booking
    ));
    const result = await save(syncLinkedBookingDates({ ...nextDoc, bookings }, linkedBookingId));
    if (!result.ok) {
      setError(result.message);
      return;
    }
    if (kind === "transfer") {
      navigate(`/journey/transfer/${nextEvent.id}`);
      return;
    }
    if (kind === "excursion") {
      navigate(`/journey/event/${nextEvent.id}`);
      return;
    }
    navigate(`/journey/day/${day}`);
  }


  const openedDay = date;
  const removalPreview = existingEvent
    ? previewEventRemoval(current, existingEvent.id)
    : existingIdea
      ? previewIdeaRemoval(existingIdea)
      : null;

  async function onRemove() {
    const result = existingEvent
      ? removeEventFromTrip(current, existingEvent.id)
      : existingIdea
        ? removeIdeaFromTrip(current, existingIdea.id)
        : { ok: false as const, error: "Nothing to remove." };
    if (!result.ok) {
      setError(result.error);
      return;
    }
    const saved = await save(result.doc);
    if (!saved.ok) {
      setError(saved.message);
      return;
    }
    navigate(`/journey/day/${existingEvent?.date ?? existingIdea?.date ?? openedDay}`);
  }

  return (
    <main className="page">
      <Link className="btn-ghost" to={editId ? `/journey/day/${openedDay}` : `/journey/day/${openedDay}/add`}>Back</Link>
      <h1 className="serif">{existingEvent || existingIdea ? "Edit this plan" : "Add to this day"}</h1>
      <form onSubmit={(e) => void onSubmit(e)}>
        <label className="field">
          Type
          <select value={kind} onChange={(e) => setKindOverride(e.target.value as PlanKind)}>
            <option value="transfer">Transfer</option>
            <option value="excursion">Excursion or plan</option>
            <option value="free_time">Free time</option>
            <option value="idea">Idea</option>
          </select>
        </label>
        <label className="field">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as PlanStatus)}>
            <option value="idea">Plan / not booked</option>
            <option value="planned">Planned</option>
            <option value="booked">Booked</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>
        <label className="field">
          Title
          <input value={title} onChange={(e) => setTitle(e.target.value)} required />
        </label>
        <label className="field">
          Day
          <input
            type="date"
            value={planDate}
            min={current.trip.startDate}
            max={current.trip.endDate}
            onChange={(e) => setPlanDate(e.target.value)}
            required
          />
        </label>
        {existingEvent && planDate && existingEvent.date !== planDate ? (
          <p className="muted">Saving moves this off {formatLongDate(existingEvent.date)} onto {formatLongDate(planDate)}.</p>
        ) : null}
        {!existingEvent && !existingIdea && planDate && openedDay && planDate !== openedDay ? (
          <p className="muted">This will be saved on {formatLongDate(planDate)}, not the day you opened.</p>
        ) : null}
        {kind === "transfer" ? (
          <label className="field">
            Method
            <select value={method} onChange={(e) => setMethod(e.target.value as TransferMethod)}>
              <option value="grab_bolt">Grab / Bolt</option>
              <option value="taxi">Taxi</option>
              <option value="arranged_driver">Arranged driver</option>
              <option value="other">Other</option>
            </select>
          </label>
        ) : null}
        {kind !== "idea" && kind !== "free_time" ? (
          <label className="field">
            Timing
            <select value={timing} onChange={(e) => setTiming(e.target.value as TimingMode)}>
              {kind === "transfer" ? (
                <>
                  <option value="after_arrival">After the flight lands</option>
                  <option value="when_ready">When we're ready</option>
                  <option value="fixed">Fixed time — confirmed</option>
                  <option value="flexible">Time still to confirm</option>
                </>
              ) : (
                <>
                  <option value="fixed">Fixed time — confirmed</option>
                  <option value="flexible">Time still to confirm</option>
                </>
              )}
            </select>
          </label>
        ) : null}
        {kind !== "idea" && (timing === "fixed" || timing === "flexible" || kind === "transfer") ? (
          <label className="field">
            {kind === "transfer"
              ? (timing === "fixed" ? "Pickup time" : "Working pickup time")
              : (timing === "flexible" ? "Working time" : "Time")}
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </label>
        ) : null}
        {kind === "transfer" ? (
          <label className="field">
            Working arrival time
            <input type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)} />
          </label>
        ) : null}
        {kind === "transfer" && timing === "after_arrival" && arrivalFlight ? (
          <p className="muted">
            {arrivalFlight.number} is due {arrivalFlight.arrival.local.slice(11, 16)} at {arrivalFlight.arrival.airport}.
            A pickup time places this transfer on the day's timeline.
          </p>
        ) : null}
        {kind === "transfer" && timing === "when_ready" ? (
          <p className="muted">Readiness still comes first. A working pickup time is only so the transfer sits in the right place on the day.</p>
        ) : null}
        {kind === "transfer" && !time && timing === "flexible" ? (
          <p className="muted">Without a pickup time this sits in the flexible part of the day.</p>
        ) : null}
        {kind !== "idea" && kind !== "transfer" && timing === "flexible" ? (
          <p className="muted">A working time stays labelled unconfirmed until you choose “Fixed time — confirmed”.</p>
        ) : null}
        {kind === "transfer" && timing === "flexible" ? (
          <p className="muted">A working pickup time stays labelled unconfirmed until you choose “Fixed time — confirmed”.</p>
        ) : null}
        {kind !== "idea" && timing === "fixed" ? (
          <p className="muted">Saving this confirms the clock time. Booking status above is separate.</p>
        ) : null}
        {kind === "transfer" ? (
          <>
            <PlaceSelect
              label="Pickup"
              value={fromPlaceId}
              onChange={(value) => {
                setFromPlaceId(value);
                if (value.startsWith("__airport__:")) setFromAirport(value.slice("__airport__:".length));
              }}
              trip={current}
              extra={(
                <>
                  {arrivalFlights.map((flight) => (
                    <option key={flight.id} value={`__airport__:${flight.arrival.airport}`}>
                      {flight.arrival.airport} airport{flight.id === arrivalFlight?.id ? " · from today's arrival" : ""}
                    </option>
                  ))}
                  {departureFlight && !arrivalFlights.some((flight) => flight.arrival.airport === departureFlight.departure.airport) ? (
                    <option value={`__airport__:${departureFlight.departure.airport}`}>
                      {departureFlight.departure.airport} airport · departure
                    </option>
                  ) : null}
                </>
              )}
            />
            {fromPlaceId === "__new__" ? (
              <PlaceLinkFields
                value={fromDraft}
                onChange={setFromDraft}
                nameLabel="Pickup place name"
              />
            ) : null}
            <PlaceSelect
              label="Destination"
              value={toPlaceId}
              onChange={setToPlaceId}
              trip={current}
            />
            {!toPlaceId && suggestedTo ? (
              <p className="muted">Suggested from tonight's stay: {suggestedTo.name}.</p>
            ) : null}
            {toPlaceId === "__new__" ? (
              <PlaceLinkFields
                value={toDraft}
                onChange={setToDraft}
                nameLabel="Destination name"
              />
            ) : null}
            <label className="field">
              Notes for the driver
              <textarea value={pickup} onChange={(e) => setPickup(e.target.value)} rows={3} placeholder="Luggage, child seat, or any personal instruction" />
            </label>
          </>
        ) : (
          <>
            <PlaceSelect label="Place" value={toPlaceId} onChange={setToPlaceId} trip={current} />
            {toPlaceId === "__new__" ? (
              <PlaceLinkFields
                value={toDraft}
                onChange={setToDraft}
                nameLabel="Place name"
              />
            ) : null}
          </>
        )}
        <label className="field">
          Notes
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />
        </label>
        {status === "booked" && (kind === "excursion" || kind === "transfer") && !existingBooking?.cost ? (
          <PlanBookingFields
            trip={current}
            draft={bookingDraft}
            onChange={setBookingDraft}
            existing={existingBooking}
          />
        ) : null}
        {status === "idea" || status === "planned" ? (
          <p className="muted">No supplier booking is required. Cost can stay unknown.</p>
        ) : null}
        {status === "booked" && ((kind !== "excursion" && kind !== "transfer") || Boolean(existingBooking?.cost)) ? (
          <p className="muted">Booked means the activity is held. A working time can still be unconfirmed.</p>
        ) : null}
        {error ? <p className="banner crit">{error}</p> : null}
        <button className="btn" type="submit" disabled={saveState === "saving"}>
          {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : "Save"}
        </button>
      </form>
      {removalPreview && !("error" in removalPreview) ? (
        <RemoveRecord
          preview={removalPreview}
          busy={saveState === "saving"}
          error={error}
          onConfirm={() => void onRemove()}
        />
      ) : null}
    </main>
  );
}

export function BookingsAddPage() {
  const { trip } = useTrip();
  const { role } = useSession();
  const [day, setDay] = useState(trip?.trip.startDate ?? "");

  if (role !== "owner") {
    return (
      <main className="page">
        <p>Owner only.</p>
        <Link to="/bookings">Back</Link>
      </main>
    );
  }
  if (!trip) return <main className="page">Loading…</main>;

  return (
    <main className="page">
      <Link className="btn-ghost" to="/bookings">Back</Link>
      <h1 className="serif">Add a plan</h1>
      <p className="muted">Plans live on a day in the journey. Costs can be linked afterward on a transfer or booking.</p>
      <label className="field">
        Which day?
        <input type="date" value={day} min={trip.trip.startDate} max={trip.trip.endDate} onChange={(e) => setDay(e.target.value)} />
      </label>
      {day ? <AddPlanChooser day={day} backTo="/bookings" /> : null}
    </main>
  );
}
